<template>
  <div>
    <section class="section">
      <div class="columns">
        <div class="column panel">
          <div class="panel-heading">
            <div class="title is-4 has-text-info uppercase mb-8">
              Votre configuration
            </div>

            <TrunkVisual
              :extremite-a="extremite_a"
              :extremite-b="extremite_b"
              :mode="mode"
              :type-de-gaine="type_de_gaine"
              :nombre-de-brins="nombre_de_brins"
              :longueur="longueur_du_lien"
            />

            <div
              v-if="type_de_gaine"
              class="is-flex justify-center mt-8"
            >
              <BaseImageWithScore
                v-for="(grade, caracteristic) in ratings"
                :key="caracteristic"
                :label="ratingIcons[caracteristic]['name']"
                :score="grade"
                :asset-url="ratingIcons[caracteristic]['picto']"
                class="mr-6"
              />
            </div>
          </div>
        </div>

        <div class="column is-one-quarter panel">
          <div class="panel-heading">
            <div class="title is-4 has-text-info uppercase mb-8">
              Parametres
            </div>
            <div class="">
              <form
                @submit.prevent="handleFormSubmit"
              >
                <b-field label="Type de fibre">
                  <b-select
                    v-model="mode"
                    required
                    placeholder="Choisir un mode"
                    @input="onModeChange(
                      mode, nombre_de_brins, type_de_gaine, extremite_a, extremite_b)"
                  >
                    <option
                      v-for="(mode_option, index) in optionsTypesDeFibre"
                      :key="index"
                      :value="mode_option"
                    >
                      {{ mode_option }}
                    </option>
                  </b-select>
                </b-field>

                <b-field label="Nombre de brins">
                  <b-select
                    v-model="nombre_de_brins"
                    required
                    placeholder="Choisir le nombre de brins"
                    :disabled="!mode"
                    @input="resetSelection(mode, nombre_de_brins, type_de_gaine)"
                  >
                    <option
                      v-for="(brin_option, index) in optionsNombreDeBrins"
                      :key="index"
                      :value="brin_option"
                      :disabled="isNombreDeBrinsDisabled(mode, brin_option)"
                    >
                      {{ brin_option }}
                    </option>
                  </b-select>
                </b-field>

                <b-field grouped>
                  <b-field
                    expanded
                    label="Connecteur A"
                  >
                    <b-select
                      v-model="extremite_a"
                      required
                      placeholder="Choisir"
                      :disabled="!mode"
                      expanded
                    >
                      <option
                        v-for="(connecteur_option, index) in optionsExtremiteA"
                        :key="index"
                        :value="connecteur_option"
                        :disabled="isConnecteurDisabled(mode, connecteur_option)"
                      >
                        {{ connecteur_option }}
                      </option>
                    </b-select>
                  </b-field>
                  <b-field
                    expanded
                    label="Connecteur B"
                  >
                    <b-select
                      v-model="extremite_b"
                      required
                      placeholder="Choisir"
                      :disabled="!mode"
                      expanded
                    >
                      <option
                        v-for="(connecteur_option, index) in optionsExtremiteB"
                        :key="index"
                        :value="connecteur_option"
                        :disabled="isConnecteurDisabled(mode, connecteur_option)"
                      >
                        {{ connecteur_option }}
                      </option>
                    </b-select>
                  </b-field>
                </b-field>


                <b-field label="Type de gaine">
                  <b-select
                    v-model="type_de_gaine"
                    required
                    :disabled="!nombre_de_brins"
                    placeholder="Choisir un type"
                    @input="resetSelection(mode, nombre_de_brins, type_de_gaine)"
                  >
                    <option
                      v-for="(gaine_option, index) in optionsTypesDeGaine"
                      :key="index"
                      :value="gaine_option"
                      :disabled="isTypeDeGaineDisabled(mode, nombre_de_brins, gaine_option)"
                    >
                      {{ gaine_option }}
                    </option>
                  </b-select>
                </b-field>
                <a
                  href="#"
                  class="text-sm"
                  @click="toggleGaineModal"
                >
                  Comment choisir ma gaine ?
                  <font-awesome-icon
                    icon="info-circle"
                  />
                </a>

                <b-field
                  class="mt-4"
                  :label="`Longueur du lien${lengthLabel}`"
                >
                  <b-input
                    v-model="longueur_du_lien"
                    required
                    type="number"
                    step="1"
                    :min="minLength"
                    :max="maxLength"
                    placeholder="Longueur en mètre"
                  />
                </b-field>

                <b-field
                  grouped
                  class="mt-8"
                  position="is-right"
                >
                  <b-button
                    type="is-success"
                    native-type="submit"
                  >
                    Suivant
                  </b-button>
                </b-field>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <b-modal
      :active.sync="isGaineModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-label="Example Modal"
      aria-modal
    >
      <template>
        <div class="card">
          <div class="card-content">
            <div class="content">
              <div class="title is-4 has-text-info uppercase">
                Comment choisir ma gaine ?
              </div>
              <div class="mb-2">
                Les caractéristiques des gaines sont notées de 0 à 5, 0 étant la qualité la plus basse et 5 la plus haute.
              </div>
              <GainesComparison />
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  TYPES_DE_GAINE, TYPES_DE_FIBRE, NOMBRE_DE_BRINS, AVAILABLE_FORM_OPTIONS,
  CONNECTEURS_A, CONNECTEURS_B, OS2_CONNECTEURS, CARACTERISTIQUES_GAINES,
  RATING_ICONS, LENGTH_BY_GAINE,
} from '@/constants.js';
import BaseImageWithScore from '@/components/BaseImageWithScore.vue';
import TrunkVisual from '@/views/partials/TrunkVisual.vue';
import GainesComparison from '@/views/GainesComparison.vue';

import { mapFields } from 'vuex-map-fields';
import { mapMutations, mapActions } from 'vuex';
import { omit, get } from 'lodash';

export default {
  name: 'Trunk',
  components: {
    BaseImageWithScore,
    TrunkVisual,
    GainesComparison,
  },
  data() {
    return {
      optionsTypesDeGaine: TYPES_DE_GAINE,
      caracteristiquesGaines: CARACTERISTIQUES_GAINES,
      optionsTypesDeFibre: TYPES_DE_FIBRE,
      optionsNombreDeBrins: NOMBRE_DE_BRINS,
      availableFormOptions: AVAILABLE_FORM_OPTIONS,
      optionsExtremiteA: CONNECTEURS_A,
      optionsExtremiteB: CONNECTEURS_B,
      lengthByGaine: LENGTH_BY_GAINE,
      ratingIcons: RATING_ICONS,
      isGaineModalActive: false,
    };
  },
  computed: {
    ...mapFields(
      'trunk',
      [
        'configuration.mode',
        'configuration.nombre_de_brins',
        'configuration.type_de_gaine',
        'configuration.extremite_a',
        'configuration.extremite_b',
        'configuration.longueur_du_lien',
      ],
    ),
    isOs2() {
      return this.mode === 'OS2';
    },
    ratings() {
      return omit(
        this.caracteristiquesGaines[this.type_de_gaine],
        ['description'],
      );
    },
    minLength() {
      return get(this.lengthByGaine[this.type_de_gaine], 'min');
    },
    maxLength() {
      return get(this.lengthByGaine[this.type_de_gaine], 'max');
    },
    lengthLabel() {
      if (this.type_de_gaine) {
        return ` (min: ${this.minLength}, max: ${this.maxLength})`;
      } return '';
    },
  },
  methods: {
    ...mapActions('trunk', ['createTrunkConfiguration']),
    ...mapMutations('trunk', ['resetTypeDeGaine', 'resetNombreDeBrins', 'resetExtremite']),
    toggleGaineModal() {
      this.isGaineModalActive = !this.isGaineModalActive;
    },
    isNombreDeBrinsDisabled(mode, nombreDeBrins) {
      if (mode) {
        const availableNombreDeBrins = Object.values(this.availableFormOptions[mode]);
        return ![].concat(...availableNombreDeBrins).includes(nombreDeBrins);
      }
      return true;
    },
    isTypeDeGaineDisabled(mode, nombreDeBrins, typeDeGaine) {
      if (mode && nombreDeBrins && this.availableFormOptions[mode][typeDeGaine]) {
        return !this.availableFormOptions[mode][typeDeGaine].includes(nombreDeBrins);
      }
      return true;
    },
    isConnecteurDisabled(mode, connecteur) {
      if (mode !== 'OS2' && (OS2_CONNECTEURS.includes(connecteur))) {
        return true;
      }
      return false;
    },
    resetSelection(mode, nombreDeBrins, typeDeGaine) {
      if (nombreDeBrins) {
        if (this.isNombreDeBrinsDisabled(mode, nombreDeBrins)) {
          this.resetNombreDeBrins();
          this.resetTypeDeGaine();
        }
      }
      if (typeDeGaine) {
        if (this.isTypeDeGaineDisabled(mode, nombreDeBrins, typeDeGaine)) {
          this.resetTypeDeGaine();
        }
      }
    },
    onModeChange(mode, nombreDeBrins, typeDeGaine, connecteurA, connecteurB) {
      this.resetSelection(mode, nombreDeBrins, typeDeGaine);
      if (this.isConnecteurDisabled(mode, connecteurA)) {
        this.resetExtremite('a');
      }
      if (this.isConnecteurDisabled(mode, connecteurB)) {
        this.resetExtremite('b');
      }
    },
    handleFormSubmit() {
      this.$router.push('/uni-trunk/options');
    },
  },
};
</script>

<style lang="scss">
.left-panel {
  max-width: 200px;
}

.preview {
  height: 200px;
}

.connector {
  position: absolute;
}

.top {
  top: 0px;
}

.bottom {
  bottom: 0px;
}

.right {
  right: 0px;
}

.left {
  left: 0px;
}

.brins {
  position: absolute;
  top: 85px;
  margin: 0px 50px 0 50px;
}

select {
  option[disabled] {
    background-color: lightgrey;
  }
}

</style>
